import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import {
  FlexBoxImageScaleView,
  FlexBoxView,
} from '@play-co/timestep-core/flexbox';
import { ImageScaleView, ImageView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { user } from 'src/StateObserver';
import { Avatar } from '../components/shared/Avatar';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { createEmitter } from 'src/lib/Emitter';
import { getBuruFriends } from 'src/replicant/friends/friends.getters';
import {
  getCurrentLeaguePhase,
  hasJoinedLeague,
} from 'src/replicant/match2/leagues/leagues.getters';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { getCurrentTeamProfile } from '../minigame/match2-timestep/team/team.sequence';
import { getMatch2Level } from 'src/replicant/match2/match2.getters';
import { GENERIC_AVATAR } from 'src/replicant/profile/assets';
import { getPlayerProfileAvatarFrame } from 'src/replicant/profile/profile.getters';

import { AssetGroup } from '@play-co/timestep-core';

const assets = {
  iconTitle: 'assets/ui/friends/profile_icon.png',
  panelInfo: 'assets/ui/friends/panel_player_info.png',
  panelStat: 'assets/ui/shared/frames/white_frame.png',
  bannerLevel: 'assets/ui/friends/banner_player_level.png',
  bannerStats: 'assets/ui/friends/banner_player_stats.png',
  iconProfile: 'assets/ui/menu/menu_action_profile.png',
  iconFriends: 'assets/ui/menu/line_pet_friends_icon_active.png',
  iconPuzzle: 'assets/ui/shared/icons/icon_puzzle.png',
};

const ui = uiConfig.buruburu.profile;

export default class PopupProfileV2 extends PopupBasic {
  private infoPanel: ImageScaleView;
  private statsContainer: FlexBoxView;

  constructor(opts: { superview: View; close: VoidFunction }) {
    super({
      superview: opts.superview,
      close: () => {
        opts.close();
      },
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 690,
      darkerBg: false,
      tracking: { feature: 'profile', $subFeature: 'popup-profile' },
      titleIcon: { image: assets.iconTitle },
      skipMessage: true,
    });

    this.title.title.style.offsetX = 15;

    this.infoPanel = new ImageScaleView({
      superview: this.box,
      image: assets.panelInfo,
      scaleMethod: '9slice' as const,
      sourceSlices: {
        horizontal: { left: 13, right: 13 },
        vertical: { top: 13, bottom: 19 },
      },
      x: this.box.style.width * 0.5,
      y: 175,
      width: 600,
      height: 169,
      centerOnOrigin: true,
    });

    const statsPanel = new ImageScaleView({
      ...uiConfig.popups.scrollBox,
      superview: this.box,
      x: this.box.style.width * 0.5,
      y: 425,
      width: 488,
      height: 249,
      centerOnOrigin: true,
    });

    this.statsContainer = new FlexBoxView({
      superview: statsPanel,
      width: statsPanel.style.width,
      height: statsPanel.style.height,
      flex: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: 30,
      },
    });

    // Stats header banner
    const statsBanner = new ImageScaleView({
      superview: statsPanel,
      image: assets.bannerStats,
      width: 262,
      height: 57,
      centerOnOrigin: true,
      x: statsPanel.style.width / 2,
      y: 13,
    });

    // Stats header label
    new LangBitmapFontTextView({
      superview: statsBanner,
      width: statsBanner.style.width,
      height: 42,
      align: 'center',
      verticalAlign: 'center',
      size: 22,
      font: 'Title',
      localeText: () => i18n('profile.generalStats'),
    });

    // Edit button
    new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      ...ui.button,
      superview: this.box,
      width: 228,
      height: 86,
      x: this.box.style.width * 0.5,
      y: this.box.style.height - 75,
      localeText: () => i18n('profile.editAction'),
      onClick: async () => {
        sounds.sfx.play('ok');
        await openPopupPromise('popupEditProfileV2', {});
      },
      tracking: {
        feature: 'profile',
        $subFeature: 'popup-profile-editProfile',
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/friends/profile_icon.png',
      'assets/ui/friends/panel_player_info.png',
      'assets/ui/shared/frames/white_frame.png',
      'assets/ui/friends/banner_player_level.png',
      'assets/ui/friends/banner_player_stats.png',
      'assets/ui/menu/menu_action_profile.png',
      'assets/ui/menu/line_pet_friends_icon_active.png',
      'assets/ui/shared/icons/icon_puzzle.png',
    ]);
  }

  public init() {
    super.init({
      title: i18n('profile.title'),
    });

    const { petRaised } = user();

    // Profile section ---------------
    const avatar = new Avatar({
      superview: this.infoPanel,
      width: 118,
      height: 119,
      dropShadow: true,
      frame: getPlayerProfileAvatarFrame(user()),
      x: 88,
      y: 82,
    });

    const name = new LangBitmapFontTextShadowView({
      superview: this.infoPanel,
      color: '#FFEE33',
      size: 32,
      align: 'left',
      verticalAlign: 'center',
      font: 'Body',
      textShadowOffsetX: 0,
      textShadowOffsetY: 4,
      textShadowColor: '#0D8378',
      width: 265,
      height: 42,
      x: 186,
      y: 57,
    });

    this.createTeamSection(name);
    this.createLevelBanner();

    createEmitter(
      this.box,
      ({ user: { publicProfile } }) => publicProfile,
    ).addListener((publicProfile) => {
      name.localeText = () => publicProfile?.name ?? '...';
      avatar.updateImage(publicProfile?.photo ?? GENERIC_AVATAR);
    });

    // Stats section ---------------
    this.createStatSection(
      assets.iconProfile,
      i18n('profile.buruburuRaised'),
      `${petRaised}`,
    );

    this.createStatSection(
      assets.iconFriends,
      i18n('footer.friends'),
      `${getBuruFriends(user())?.length ?? 0}`,
    );
  }

  private async createTeamSection(nameLabel: LangBitmapFontTextShadowView) {
    const team = await getCurrentTeamProfile()?.get();

    if (!team) return;

    nameLabel.style.y = 38;

    const teamAvatarSize = 38;

    // Team avatar
    const teamAvatar = new Avatar({
      superview: this.infoPanel,
      image: team.icon,
      width: teamAvatarSize,
      height: teamAvatarSize,
      borderThickness: 4,
      x: nameLabel.style.x + teamAvatarSize * 0.5,
      y: nameLabel.style.y + nameLabel.style.height + teamAvatarSize * 0.5 + 7,
    });

    // Team name
    new LangBitmapFontTextShadowView({
      superview: this.infoPanel,
      color: '#FFFFFF',
      size: 24,
      align: 'left',
      verticalAlign: 'center',
      font: 'Body',
      textShadowOffsetX: 0,
      textShadowOffsetY: 4,
      textShadowColor: '#0D8378',
      width: 215,
      height: 35,
      x: teamAvatar.style.x + teamAvatarSize * 0.5 + 10,
      y: teamAvatar.style.y - 17,
      localeText: () => `${team.name}`,
    });
  }

  private createLevelBanner() {
    const levelBanner = new ImageScaleView({
      superview: this.infoPanel,
      image: assets.bannerLevel,
      width: 103,
      height: 118,
      centerOnOrigin: true,
      x: this.infoPanel.style.width - 78,
      y: 88,
    });

    // Puzzle icon
    new ImageView({
      superview: levelBanner,
      image: assets.iconPuzzle,
      width: 45,
      height: 45,
      centerOnOrigin: true,
      x: levelBanner.style.width * 0.5,
      y: 10,
    });

    const leagueJoined = hasJoinedLeague(user());

    // Level label
    new LangBitmapFontTextShadowView({
      superview: levelBanner,
      color: '#FFEE33',
      size: 22,
      align: 'center',
      verticalAlign: 'center',
      font: 'Body',
      textShadowOffsetX: 0,
      textShadowOffsetY: 4,
      textShadowColor: '#911717',
      width: levelBanner.style.width,
      height: 30,
      x: -1,
      y: 29,
      localeText: () => i18n(`profile.${leagueJoined ? 'phase' : 'level'}`),
    });

    // Level value
    new LangBitmapFontTextShadowView({
      superview: levelBanner,
      color: '#FFFFFF',
      size: 22,
      align: 'center',
      verticalAlign: 'center',
      font: 'Body',
      textShadowOffsetX: 0,
      textShadowOffsetY: 4,
      textShadowColor: '#911717',
      width: levelBanner.style.width,
      height: 30,
      x: -2,
      y: 55,
      localeText: () =>
        `${leagueJoined ? getCurrentLeaguePhase(user()) : getMatch2Level(user())}`,
    });
  }

  private createStatSection(icon: string, label: string, data: string) {
    const panel = new FlexBoxImageScaleView({
      superview: this.statsContainer,
      image: assets.panelStat,
      imgScale: 0.65,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 47, right: 47 },
        vertical: { top: 47, bottom: 47 },
      },
      width: 454,
      height: 87,
      flex: {
        marginVertical: 3,
      },
    });

    const container = new FlexBoxView({
      superview: panel,
      width: panel.style.width,
      height: panel.style.height,
      flex: {
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: 21,
        paddingRight: 40,
        paddingBottom: 5,
      },
    });

    // Stat icon
    new ImageScaleView({
      superview: container,
      image: icon,
      width: 52,
      height: 52,
      scaleMethod: 'contain',
    });

    // Stat label
    new LangBitmapFontTextView({
      superview: container,
      color: '#BF8147',
      size: 22,
      align: 'left',
      verticalAlign: 'center',
      font: 'Body',
      width: 200,
      height: panel.style.height,
      localeText: () => label,
    });

    // Stat data
    new LangBitmapFontTextView({
      superview: container,
      color: '#9A7171',
      size: 26,
      align: 'right',
      verticalAlign: 'center',
      font: 'Body',
      width: 110,
      height: panel.style.height,
      localeText: () => data,
    });
  }
}
